// v2 workshop — a craftsman's court: an open-sided timber hall at the back of the
// 3x3 plot — six posts under a gable roof, a plank tool wall hung with hammer,
// saw and tongs, two workbenches with a vice — and a working yard in front with a
// domed stone kiln (glowing fire mouth and flue), an anvil on a stump, a quench
// barrel, a log pile and loose planks. Footprint-native 3x3. Ported 1:1 from
// age-of-mcp's v2/workshop.ts, using the library's shared `foundationSlab`,
// `barrel` and `village` (TIMBER, buildScaffold, gableRoof) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, barrel, village, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const BASE = 0.14;
const HX = -0.2;             // hall centre x
const HZ = -0.75;            // hall centre z (back of the plot)
const HALL_W = 1.8, HALL_D = 0.8;
const PLANK = "#b59a6a";
const BENCH = "#9a7440";
const IRON  = "#4a4f55";
const KX = 0.85, KZ = 0.72;  // kiln corner

// The "kiln is fired" cue: smoke rising off the kiln flue (it tops out at
// y = 1.23) while the fire mouth pulses.
const plume = smokePlume([KX, 1.24, KZ], { rise: 0.36, radius: 0.05 });

// The six hall posts, `h` tall.
function posts(h: number): Part[] {
  const out: Part[] = [];
  for (const x of [HX - HALL_W / 2, HX, HX + HALL_W / 2]) {
    for (const z of [HZ - HALL_D / 2, HZ + HALL_D / 2]) {
      out.push({ geometry: new THREE.BoxGeometry(0.08, h, 0.08), color: village.TIMBER,
        position: [x, BASE + h / 2, z] });
    }
  }
  return out;
}

// The plank tool wall closing the hall's back, `h` tall.
function toolWall(h: number): Part {
  return { geometry: new THREE.BoxGeometry(HALL_W + 0.08, h, 0.07), color: PLANK,
    position: [HX, BASE + h / 2, HZ - HALL_D / 2 + 0.03] };
}

// A workbench: a top on two trestle legs, with something on it.
function workbench(x: number, z: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.5, 0.05, 0.3), color: BENCH,
      position: [x, 0.42, z] },
    { geometry: new THREE.BoxGeometry(0.06, 0.26, 0.26), color: village.TIMBER,
      position: [x - 0.19, 0.28, z] },
    { geometry: new THREE.BoxGeometry(0.06, 0.26, 0.26), color: village.TIMBER,
      position: [x + 0.19, 0.28, z] },
  ];
}

// The stone kiln (base course + dome + cone flue); built up by `level`.
function kiln(level: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.CylinderGeometry(0.34, 0.40, 0.34, 8), color: "#7e7a74",
      position: [KX, 0.31, KZ] },
  ];
  if (level >= 2) {
    out.push({ geometry: new THREE.CylinderGeometry(0.22, 0.33, 0.26, 8), color: "#8a857e",
      position: [KX, 0.61, KZ] });
  }
  if (level >= 3) {
    out.push(
      { geometry: new THREE.ConeGeometry(0.20, 0.28, 8), color: "#7e7a74",
        position: [KX, 0.88, KZ] },
      { geometry: new THREE.CylinderGeometry(0.05, 0.05, 0.30, 6), color: "#3a3a3a",
        position: [KX, 1.08, KZ] },
      ...plume.parts,
      // Glowing fire mouth on the yard side.
      { geometry: new THREE.BoxGeometry(0.16, 0.14, 0.06), color: "#f08a2c",
        position: [KX, 0.30, KZ + 0.36], name: "kiln-fire" },
    );
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(2.94)];
  if (s === 1) {
    return [foundationSlab(2.94), ...posts(0.4), toolWall(0.3),
      ...village.buildScaffold(2.0, 1.0, 0.7, HX, HZ)];
  }
  if (s === 2) {
    return [foundationSlab(2.94), ...posts(0.78), toolWall(0.62),
      ...workbench(-0.7, HZ), ...workbench(0.25, HZ), ...kiln(2),
      ...village.buildScaffold(2.0, 1.0, 1.2, HX, HZ)];
  }

  const top = BASE + 0.78;               // post tops carry the plates
  const wallFace = HZ - HALL_D / 2 + 0.07;
  return [
    foundationSlab(2.94),
    ...posts(0.78),
    toolWall(0.62),
    // Plate beams along the post rows, carrying the roof.
    { geometry: new THREE.BoxGeometry(HALL_W + 0.12, 0.07, 0.09), color: village.TIMBER,
      position: [HX, top + 0.035, HZ + HALL_D / 2] },
    { geometry: new THREE.BoxGeometry(HALL_W + 0.12, 0.07, 0.09), color: village.TIMBER,
      position: [HX, top + 0.035, HZ - HALL_D / 2] },
    ...village.gableRoof({ cx: HX, cz: HZ, topY: top + 0.07, span: HALL_D, length: HALL_W + 0.12, rise: 0.42 }),
    // Tools hung on the back wall: hammer, saw, tongs.
    { geometry: new THREE.BoxGeometry(0.025, 0.14, 0.02), color: BENCH,
      position: [-0.75, 0.52, wallFace] },
    { geometry: new THREE.BoxGeometry(0.09, 0.035, 0.04), color: IRON,
      position: [-0.75, 0.60, wallFace] },
    { geometry: new THREE.BoxGeometry(0.22, 0.06, 0.015), color: "#9aa2a8",
      position: [-0.35, 0.55, wallFace], rotation: [0, 0, 0.15] },
    { geometry: new THREE.BoxGeometry(0.04, 0.18, 0.015), color: IRON,
      position: [0.18, 0.52, wallFace] },
    // Two workbenches under the hall, a vice on the left one.
    ...workbench(-0.7, HZ),
    ...workbench(0.25, HZ),
    { geometry: new THREE.BoxGeometry(0.07, 0.09, 0.07), color: IRON,
      position: [-0.52, 0.49, HZ] },
    // The kiln corner: kiln, anvil on a stump, quench barrel.
    ...kiln(3),
    { geometry: new THREE.BoxGeometry(0.16, 0.18, 0.16), color: village.TIMBER,
      position: [0.45, 0.23, 0.55] },
    { geometry: new THREE.BoxGeometry(0.22, 0.08, 0.09), color: IRON,
      position: [0.45, 0.36, 0.55] },
    barrel(0.42, 0.95),
    // Log pile and loose planks at the front-left of the court.
    { geometry: new THREE.CylinderGeometry(0.055, 0.055, 0.8, 6), color: "#7a5a34",
      position: [-0.85, 0.195, 0.75], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.055, 0.055, 0.8, 6), color: "#6e4f2c",
      position: [-0.85, 0.195, 0.92], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.055, 0.055, 0.8, 6), color: "#7a5a34",
      position: [-0.85, 0.30, 0.835], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.BoxGeometry(0.55, 0.05, 0.22), color: PLANK,
      position: [-0.78, 0.165, 0.42] },
    { geometry: new THREE.BoxGeometry(0.55, 0.05, 0.22), color: "#a88a5a",
      position: [-0.78, 0.215, 0.42], rotation: [0, 0.2, 0] },
  ];
}

export const workshopV2 = defineModel({
  id: "workshop-v2",
  tiles: { w: 3, h: 3 },
  batchId: "fable",
  name: "Workshop (v2)",
  tags: ["building","workshop","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly workshop.",
  description: "A walled crafting compound of benches under a tiled roof.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Kiln fired",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, firePulse("kiln-fire", [KX, 0.30, KZ + 0.36])],
    },
  ],
});
